import slug from "../slug";

const getFormKey = (index = 1) => {
  const depth = {
    1: 'category',
    2: 'objet',
    3: 'reason',
    4: 'sub_reason',
    5: 'reason_need',
    6: 'sub_reason_need',
  };

  return depth[index];
};

const getCategoryKey = (questionLevel, questionSlug) => {
  const formKey = getFormKey(questionLevel);
  return `${formKey}_${questionSlug}`;
};

const buildCumulativeConditionals = (pathFromRoot) => {
  return pathFromRoot.reduce((conditionals, ancestor) => {
    const formKey = getFormKey(ancestor.level);
    conditionals[`cf-conditional-${formKey}`] = `${formKey}_${ancestor.categorySlug}`;
    return conditionals;
  }, {});
};

const buildCustomerQuestion = (customerField, cumulativeConditionals) => {
  const fieldName = slug(customerField.name);
  const fieldType = customerField.type || 'text';

  return {
    tag: 'input',
    type: fieldType,
    name: fieldName,
    ...cumulativeConditionals,
    'cf-questions': `Veuillez saisir votre ${customerField.name.toLowerCase()}`,
    'cf-input-placeholder': 'Entrez votre réponse ici...',
    'cf-validation': 'required',
    'cf-error': 'Cette information est requise.',
  };
};

const buildQuestion = (pathFromRoot, customerFields) => {
  const leafNode = pathFromRoot[pathFromRoot.length - 1];
  const leafFormKey = getFormKey(leafNode.level);
  const leafValue = `${leafFormKey}_${leafNode.categorySlug}`;
  const finishStepFileConditional = `cf-conditional-${leafFormKey}_${leafNode.categorySlug}_forFinishStep`;

  const cumulativeConditionals = buildCumulativeConditionals(pathFromRoot);

  const customerQuestions = customerFields.map(
    field => buildCustomerQuestion(field, cumulativeConditionals)
  );

  return [
    ...customerQuestions,
    {
      tag: 'input',
      type: 'text',
      name: 'comment',
      ...cumulativeConditionals,
      'cf-questions': 'Veuillez fournir des détails sur votre demande',
      'cf-input-placeholder': '(Date de l’opération, informations complémentaires…)',
      'cf-validation': 'required',
      'cf-error': 'Cette information est requise.',
    },
    {
      tag: 'select',
      type: 'text',
      name: `${leafValue}_forFinishStep`,
      ...cumulativeConditionals,
      'cf-questions': 'Avez-vous des fichiers à joindre ? ',
      children: [
        { tag: 'option', 'cf-label': 'Oui', value: 'orange_rdc_bot_upload_file_yes' },
        { tag: 'option', 'cf-label': 'Non', value: 'upload_file_no' },
      ],
    },
    {
      tag: 'input',
      type: 'file',
      multiple: true,
      name: 'files',
      [finishStepFileConditional]: 'orange_rdc_bot_upload_file_yes',
      'cf-questions': 'Cliquez sur le bouton ci-dessous pour ajouter les fichiers',
      'cf-validation': 'required',
      'cf-error': 'Cette information est requise.',
    },
  ];
};

const walkInCategories = (categories, customerFields, parentChain = []) => {
  const questions = [];

  const magneticFormCategories = [
    'reason_need_atm', 'reason_need_tpe', 'reason_need_en_ligne',
    'sub_reason_rejet_atm', 'sub_reason_rejet_tpe', 'sub_reason_rejet_en_ligne',
    'sub_reason_double_prelevement', 'sub_reason_transaction_non_percue', 'sub_reason_debit_a_tort',
  ];

  for (const category of categories) {
    const level = parentChain.length + 1;
    const categorySlug = slug(category.title);
    const categoryKey = getCategoryKey(level, categorySlug);

    if (categorySlug === 'code_swift_inexistant') {
      continue;
    }

    if (magneticFormCategories.includes(categoryKey)) {
      continue;
    }

    const currentChain = [...parentChain, { level, categorySlug }];

    if (category.children.length === 0) {
      questions.push(...buildQuestion(currentChain, customerFields));
    } else {
      questions.push(...walkInCategories(category.children, customerFields, currentChain));
    }
  }

  return questions;
};

const groupCategoriesByParent = (categories) => {
  const categoryMap = {};
  const rootCategories = [];

  categories.forEach(category => {
    category.children = [];
    categoryMap[category.id] = category;
  });

  categories.forEach(category => {
    if (category.parent_id === "" || category.parent_id === null) {
      category.parent_id = null;
    }

    if (category.parent_id === null) {
      rootCategories.push(category);
    } else {
      const parent = categoryMap[category.parent_id];
      if (parent) {
        parent.children.push(category);
      }
    }
  });

  return rootCategories;
};

const createQuestionsPerFlow = (categories, customerFields = []) => {
  const rootCategories = groupCategoriesByParent(categories);
  return walkInCategories(rootCategories, customerFields);
};

export default createQuestionsPerFlow;
